import algoliasearch from "algoliasearch/lite";
import instantsearch from "instantsearch.js";
import {
  configure,
  searchBox,
  pagination,
  hierarchicalMenu,
  currentRefinements,
} from "instantsearch.js/es/widgets";
import { connectHits } from "instantsearch.js/es/connectors";
import { history } from "instantsearch.js/es/lib/routers";
import Mustache from "mustache";
import { debounce } from 'vendor/lodash.debounce';
import guideTemplate from "templates/guides/guide.mustache";

export default class Search {
  constructor() {
    // move this to js global vars
    this.$searchInfo = $("#search-info");
    this.app_id = this.$searchInfo.data("app-id");
    this.api_key = this.$searchInfo.data("app-key");
    this.appIndex = this.$searchInfo.data("app-index");
    this.app_uri = this.$searchInfo.data("app-uri");

    this.originalLocation = location.href;

    this.client = this._initClient();
    this.search = this._initSearch();

    this._addWidgets();
    this.search.start();

    // we need to let the video controller know that a search has happened so we
    // can play the videos
    this.search.on("render", () => {
      const event = new CustomEvent("algolia-search", { bubbles: true });
      document.dispatchEvent(event);
    });
  }

  _initSearch() {
    const originalLocation = this.originalLocation;
    return instantsearch({
      searchClient: this.client,
      indexName: this.appIndex,
      routing: {
        router: history({
          // match route to search?q=ada
          createURL({ qsModule, routeState, location }) {
            const urlParts = location.href.match(/^(.*)\/search(\?.*|$)/);
            const baseUrl = `${urlParts ? urlParts[1] : ""}/`;

            const queryParameters = {};

            if (routeState.query) {
              queryParameters.q = encodeURIComponent(routeState.query);
            }
            if (routeState.page !== 1) {
              queryParameters.page = routeState.page;
            }

            if (routeState.categoriesLvl0) {
              queryParameters.categoriesLvl0 = encodeURIComponent(
                routeState.categoriesLvl0,
              );
            }
            if (routeState.categoriesLvl1) {
              queryParameters.categoriesLvl1 = encodeURIComponent(
                routeState.categoriesLvl1,
              );
            }

            const queryString = qsModule.stringify(queryParameters, {
              addQueryPrefix: true,
              arrayFormat: "repeat",
            });

            // return to original location if no refinements
            if (
              queryString == "" &&
              !originalLocation.match(/^(.*)\/search(\?.*|$)/)
            ) {
              return originalLocation;
            } else {
              return `${baseUrl}search${queryString}`;
            }
          },

          parseURL({ qsModule, location }) {
            const {
              q = "",
              page,
              categoriesLvl0 = "",
              categoriesLvl1 = "",
            } = qsModule.parse(location.search.slice(1));

            return {
              query: decodeURIComponent(q),
              "categories.lvl0": decodeURIComponent(categoriesLvl0),
              "categories.lvl1": decodeURIComponent(categoriesLvl1),
              page,
            };
          },
        }),

        stateMapping: {
          stateToRoute(uiState) {
            var appIndex = $("#search-info").data("app-index");
            const indexUiState = uiState[appIndex] || {};

            var categoriesLvl0, categoriesLvl1;

            // hierarchicalMenu returns "categories.lvl0": [parent_name, child_name]
            if (
              indexUiState.hierarchicalMenu &&
              indexUiState.hierarchicalMenu["categories.lvl0"]
            ) {
              categoriesLvl0 =
                indexUiState.hierarchicalMenu["categories.lvl0"][0];
              categoriesLvl1 =
                indexUiState.hierarchicalMenu["categories.lvl0"][1];
            }

            return {
              query: indexUiState.query,
              categoriesLvl0: categoriesLvl0,
              categoriesLvl1: categoriesLvl1,
              page: indexUiState.page,
            };
          },

          routeToState(routeState) {
            var appIndex = $("#search-info").data("app-index");
            var state = {};

            var combinedCategories = [
              routeState["categories.lvl0"],
              routeState["categories.lvl1"],
            ].filter(Boolean);
            combinedCategories = combinedCategories.length
              ? combinedCategories
              : "";

            state[appIndex] = {
              query: routeState.query,
              hierarchicalMenu: {
                "categories.lvl0": combinedCategories,
              },
              page: routeState.page,
            };

            return state;
          },
        },
      },
    });
  }

  _addWidgets() {
    // use guide mustache template
    const guideHits = connectHits(function renderHits(
      { hits, widgetParams },
      isFirstRendering,
    ) {
      const container = widgetParams.container;

      if (hits.length) {
        $(container).html(
          hits.map((hit) => Mustache.render(guideTemplate, hit)),
        );
      } else {
        $(container).html(widgetParams.templates.empty);
      }
    });

    this.search.addWidgets([
      configure({
        hitsPerPage: 21,
        distinct: true,
      }),

      searchBox({
        container: "#search",
        showReset: false,
        showSubmit: false,
        queryHook: debounce(function(query, search) {
          search(query);
        }, 250),
      }),

      currentRefinements({
        container: "#search-refinements",
        includedAttributes: ["query", "categories.lvl0", "categories.lvl1"],
        cssClasses: {
          label: "refinement-label",
          category: "refinement",
          delete: "delete-refinement",
        },
        transformItems(items) {
          const newItems = items.map((item) => {
            if (item.attribute == "categories.lvl0") {
              const newItem = item;
              newItem["label"] = "category";
              return newItem;
            } else {
              return item;
            }
          });

          return newItems;
        },
      }),

      guideHits({
        container: "#search-results",
        templates: {
          empty: '<div class="no-results">No results for query</div>',
        },
      }),

      hierarchicalMenu({
        container: "#search-categories",
        attributes: ["categories.lvl0", "categories.lvl1"],
        limit: 100,
        cssClasses: {
          root: "root",
          // count: 'count',
          selectedItem: "selected",
          parentItem: "parent",
          childList: "children",
        },
        templates: {
          // remove count because facetingAfterDistinct does not unique count correctly
          item: `
              <a class="{{cssClasses.link}}" href="{{url}}">
                <span class="{{cssClasses.label}}">{{label}}</span>
              </a>
            `,
        },
      }),

      pagination({
        container: "#search-pagination",
        cssClasses: {
          item: "item",
          link: "link",
          selectedItem: "selected",
          disabledItem: "disabled",
        },
      }),
    ]);
  }

  _initClient() {
    const algoliaClient = algoliasearch(this.app_id, this.api_key);
    const originalLocation = this.originalLocation;

    return {
      search(requests) {
        // don't render search until query/refienment entered
        if (
          requests.every(({ params }) => !params.query && !params.facetFilters)
        ) {
          document
            .querySelector("#main-content")
            .classList.remove("search-active");
          // stub initial search query
          return Promise.resolve({
            results: requests.map(() => ({
              hits: [],
              nbHits: 0,
              nbPages: 0,
            })),
          });
        } else {
          document
            .querySelector("#main-content")
            .classList.add("search-active");
        }

        return algoliaClient.search(requests);
      },
    };
  }
}
